import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Printer, UsersRound, AlertTriangle } from "lucide-react";
import { labelFuncion } from "@/lib/sepCatalogos";

const FUNCION_LABEL_CORTO = {
  SUBDIRECTOR_GESTION_ESCOLAR: "SUBDIRECTOR",
  SUBDIRECTOR_ACADEMICO: "SUBDIRECTOR",
  PROFESOR_EDUCACION_FISICA: "E. FÍSICA",
  PROFESOR_ARTISTICAS: "ARTÍSTICA",
  PROFESOR_TECNOLOGICAS: "TECNOLOGÍA",
  PROFESOR_IDIOMAS: "INGLÉS",
  INTENDENTE: "INTENDENTE",
  APOYO_ADMINISTRATIVO: "ADMINISTRATIVO",
  ADMINISTRADOR: "ADMINISTRATIVO",
  SECRETARIA: "ADMINISTRATIVO",
};

const ORDEN_CARGO = {
  DIRECTOR: 0,
  SUBDIRECTOR: 1,
  ADMINISTRATIVO: 2,
  "E. FÍSICA": 3,
  "INGLÉS": 4,
  "TECNOLOGÍA": 5,
  "ARTÍSTICA": 6,
  INTENDENTE: 90,
};

const computeEdad = (fnac) => {
  if (!fnac) return "";
  const d = new Date(fnac);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a -= 1;
  return a >= 0 && a < 130 ? String(a) : "";
};

const computeAntiguedad = (fingreso) => {
  if (!fingreso) return "";
  const d = new Date(fingreso);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a -= 1;
  if (a < 0) return "";
  return a === 1 ? "1 año" : `${a} años`;
};

const nombreCompleto = (u) => u ? [u.apellido_paterno, u.apellido_materno, u.nombre].filter(Boolean).join(" ").toUpperCase() : "";
const fmtFecha = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export default function PersonalEscuela() {
  const { user } = useAuth();
  const [planteles, setPlanteles] = useState([]);
  const [plantelId, setPlantelId] = useState("");
  const [docentes, setDocentes] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [personalFn, setPersonalFn] = useState([]);
  const [director, setDirector] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/planteles");
        setPlanteles(data);
        setPlantelId(data[0]?.id || "");
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  useEffect(() => {
    if (!plantelId) return;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/planteles/${plantelId}/staff-list`);
        setDocentes(data.docentes || []);
        setGrupos(data.grupos || []);
        setPersonalFn(data.personal_funcion || []);
        // Director: si el usuario actual es el director del plantel, usa datos de useAuth
        if (data.plantel?.director_user_id && user?.id === data.plantel.director_user_id) {
          setDirector(user);
        } else {
          setDirector(data.director || null);
        }
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [plantelId, planteles, user]);

  const plantel = planteles.find((p) => p.id === plantelId);

  const rows = useMemo(() => {
    const out = [];
    if (director) {
      out.push({
        cargo: "DIRECTOR",
        orden: 0,
        nombre: nombreCompleto(director),
        plaza: director.plaza || "",
        curp: director.curp || "",
        rfc: director.rfc || "",
        edad: computeEdad(director.fecha_nacimiento),
        fecha_ingreso: fmtFecha(director.fecha_ingreso_sep),
        antiguedad: computeAntiguedad(director.fecha_ingreso_sep),
        correo: director.correo_personal || director.email || "",
        celular: director.celular || director.telefono || "",
      });
    }
    // Docentes titulares por grupo (grado)
    const docentesById = new Map(docentes.map((d) => [d.id, d]));
    const gruposOrd = [...grupos].sort((a, b) => (a.grado - b.grado) || String(a.letra).localeCompare(String(b.letra)));
    gruposOrd.forEach((g) => {
      const d = docentesById.get(g.docente_id);
      if (!d) return;
      out.push({
        cargo: `DOCENTE ${g.grado}°${g.letra ? ` "${g.letra}"` : ""}`,
        orden: 10 + g.grado,
        nombre: nombreCompleto(d),
        plaza: d.plaza || "",
        curp: d.curp || "",
        rfc: d.rfc || "",
        edad: computeEdad(d.fecha_nacimiento),
        fecha_ingreso: fmtFecha(d.fecha_ingreso_sep),
        antiguedad: computeAntiguedad(d.fecha_ingreso_sep),
        correo: d.correo_personal || d.email || "",
        celular: d.celular || d.telefono || "",
      });
    });
    // Personal por función (subdirector, e.física, inglés, tec., artísticas, admin, intendente, otros)
    personalFn.forEach((p) => {
      const cargoCorto = FUNCION_LABEL_CORTO[p.funcion] || labelFuncion(p.funcion).toUpperCase();
      out.push({
        cargo: cargoCorto,
        orden: ORDEN_CARGO[cargoCorto] ?? 50,
        nombre: [p.apellido_paterno, p.apellido_materno, p.nombre].filter(Boolean).join(" ").toUpperCase(),
        plaza: p.plaza || "",
        curp: p.curp || "",
        rfc: p.rfc || "",
        edad: computeEdad(p.fecha_nacimiento),
        fecha_ingreso: fmtFecha(p.fecha_ingreso_sep),
        antiguedad: computeAntiguedad(p.fecha_ingreso_sep),
        correo: p.correo_personal || "",
        celular: p.celular || "",
      });
    });
    out.sort((a, b) => a.orden - b.orden);
    return out;
  }, [director, docentes, grupos, personalFn]);

  const printPage = () => window.print();

  return (
    <AppLayout>
      <div className="max-w-[1400px]" data-testid="personal-escuela-page">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3 print:hidden">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-1">Directorio del plantel</div>
            <h1 className="text-3xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Personal de la Escuela
            </h1>
            <p className="text-sm text-[#545454] mt-1">Se auto-completa con el Director asignado, los Docentes titulares por grupo y el Personal por función registrado.</p>
          </div>
          <div className="flex items-center gap-2">
            {["superadmin", "jefe_sector", "supervisor_zona"].includes(user?.role) && planteles.length > 0 && (
              <Select value={plantelId} onValueChange={setPlantelId}>
                <SelectTrigger data-testid="plantel-selector" className="h-9 w-72"><SelectValue placeholder="Plantel" /></SelectTrigger>
                <SelectContent>
                  {planteles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button data-testid="btn-print-personal-escuela" onClick={printPage} className="bg-[#235B4E] hover:bg-[#1D4A40] text-white">
              <Printer className="w-4 h-4 mr-1" /> Imprimir / PDF
            </Button>
          </div>
        </div>

        <div className="bg-white border-2 border-black print:border-black" style={{ fontFamily: "'Times New Roman', Times, serif" }}>
          <div className="flex items-center justify-between px-4 py-2 border-b-2 border-black gap-4">
            <img
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_edu-censo-admin/artifacts/8c23fucn_Logo%20Censo911.png"
              alt="Censo 911"
              className="h-14 object-contain"
              crossOrigin="anonymous"
            />
            <div className="text-center flex-1">
              <div className="text-[10px] tracking-widest text-[#374151]">SISTEMA CENSO 911 · TAMAULIPAS</div>
              <div className="text-lg font-bold text-[#10312B]" style={{ fontFamily: "'Montserrat', sans-serif" }}>PERSONAL DE LA ESCUELA</div>
            </div>
            <div className="w-14" />
          </div>
          <div className="px-4 py-2 border-b border-black text-[12px] grid grid-cols-3 gap-x-6 gap-y-1">
            <div><b>NOMBRE DE LA ESCUELA:</b> {(plantel?.nombre || "").toUpperCase()}</div>
            <div><b>C.C.T.:</b> <span className="font-mono">{plantel?.cct || ""}</span></div>
            <div><b>TURNO:</b> {(plantel?.turno || "").toUpperCase()}</div>
          </div>
          {(() => {
            const missing = (v) => !v || !String(v).trim();
            const faltantes = rows.filter((r) => missing(r.curp) || missing(r.rfc) || missing(r.correo)).length;
            if (!faltantes) return null;
            return (
              <div data-testid="pe-alerta-faltantes" className="px-4 py-2 border-b border-[#FCA5A5] bg-[#FEF2F2] text-[12px] text-[#9F2241] flex items-center gap-2 print:hidden">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span><b>{faltantes}</b> persona{faltantes === 1 ? "" : "s"} con datos incompletos. Las celdas resaltadas indican falta de CURP, R.F.C. o Correo.</span>
              </div>
            );
          })()}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[11px]">
              <thead>
                <tr className="bg-[#F3F4F6]">
                  {["No.", "CARGO", "NOMBRE", "PLAZA/CLAVE", "CURP", "R.F.C.", "EDAD", "FECHA DE INGRESO", "ANTIGÜEDAD", "CORREO", "PERSONAL CELULAR"].map((h) => (
                    <th key={h} className="border border-black p-1.5 text-center align-middle font-bold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={11} className="text-center py-10 text-[#9CA3AF]">Cargando…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={11} className="text-center py-10 text-[#9CA3AF]">
                    <UsersRound className="w-6 h-6 mx-auto mb-2" />
                    Sin personal registrado todavía.
                  </td></tr>
                ) : rows.map((r, i) => {
                  const missing = (v) => !v || !String(v).trim();
                  const mCurp = missing(r.curp);
                  const mRfc = missing(r.rfc);
                  const mCorreo = missing(r.correo);
                  const missCls = "bg-[#FEE2E2] text-[#9F2241] font-semibold";
                  return (
                    <tr key={`${r.cargo}-${i}`} className="align-middle">
                      <td className="border border-black p-1 text-center">{i + 1}</td>
                      <td className="border border-black p-1 font-semibold">{r.cargo}</td>
                      <td className="border border-black p-1">{r.nombre || <span className="text-[#9F2241] italic">Sin asignar</span>}</td>
                      <td className="border border-black p-1 font-mono text-center">{r.plaza}</td>
                      <td className={`border border-black p-1 font-mono ${mCurp ? missCls : ""}`}>{mCurp ? "FALTA" : r.curp}</td>
                      <td className={`border border-black p-1 font-mono ${mRfc ? missCls : ""}`}>{mRfc ? "FALTA" : r.rfc}</td>
                      <td className="border border-black p-1 text-center">{r.edad}</td>
                      <td className="border border-black p-1 text-center">{r.fecha_ingreso}</td>
                      <td className="border border-black p-1 text-center">{r.antiguedad}</td>
                      <td className={`border border-black p-1 ${mCorreo ? missCls : ""}`}>{mCorreo ? "FALTA" : r.correo}</td>
                      <td className="border border-black p-1">{r.celular}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-3 text-[11px] text-[#6B7280] print:hidden">
          Los datos se completan automáticamente desde: Director asignado al plantel, Docentes titulares (Grupos) y Personal por función. Para actualizar, edita cada persona en su sección correspondiente.
        </div>
      </div>
      <style>{`
        @page { size: Letter landscape; margin: 6mm; }
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          html, body { margin: 0 !important; padding: 0 !important; }
        }
      `}</style>
    </AppLayout>
  );
}
